import * as React from 'react';
import { Decimal } from 'decimal.js';
import { Temporal } from '@insio/dates';
import type { CurrencyCode } from '@insio/shared-types';
import { InvoicePurpose, InvoiceType, PaymentMethod, VatMode } from '@insio/shared-types';
import { FieldType } from '../../../../types';
import { AddressEditor } from '../editors/AddressEditor';
import { BankingDetailsEditor } from '../editors/BankingDetailsEditor';
import { BooleanEditor } from '../editors/BooleanEditor';
import { CountryEditor } from '../editors/CountryEditor';
import { CurrencyEditor } from '../editors/CurrencyEditor';
import { DateEditor } from '../editors/DateEditor';
import { InvoiceLinesEditor } from '../editors/InvoiceLinesEditor';
import { InvoicePurposeEditor } from '../editors/InvoicePurposeEditor';
import { InvoiceTypeEditor } from '../editors/InvoiceTypeEditor';
import { VatModeEditor } from '../editors/VatModeEditor';
import { PaymentMethodEditor } from '../editors/PaymentMethodEditor';
import { DecimalEditor } from '../editors/DecimalEditor';
import { TextAreaEditor } from '../editors/TextAreaEditor';
import { TextEditor } from '../editors/TextEditor';
import { PartnerInlineEditor } from '../editors/PartnerInlineEditor';
import { VatSummaryEditor } from '../editors/VatSummaryEditor';
import { InvoiceTotalsEditor } from '../editors/InvoiceTotalsEditor';
import { AddressFormatter } from '../formatters/AddressFormatter';
import { BankingDetailsFormatter } from '../formatters/BankingDetailsFormatter';
import { BooleanFormatter } from '../formatters/BooleanFormatter';
import { CountryFormatter } from '../formatters/CountryFormatter';
import { CurrencyFormatter } from '../formatters/CurrencyFormatter';
import { DateFormatter } from '../formatters/DateFormatter';
import { InvoiceLinesFormatter } from '../formatters/InvoiceLinesFormatter';
import { createCodelistFormatter } from '../formatters/CodelistFormatter';
import { DecimalFormatter } from '../formatters/DecimalFormatter';
import { TextFormatter } from '../formatters/TextFormatter';
import { VatSummaryFormatter } from '../formatters/VatSummaryFormatter';
import { InvoiceTotalsFormatter } from '../formatters/InvoiceTotalsFormatter';
import { EditorConfig, FormatterProps } from '../types/editor-config';
import { isVatSummary, isVatSummaryItem } from './vat-summary';

export interface ResolveEditorConfigOptions {
  /** Required when `fieldType === 'decimal'`. Bound into the resolved
   * `DecimalFormatter` so it can call `useFormatAmount(currencyCode)`. */
  currencyCode?: CurrencyCode;
}

const decimalValueGuard = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  if (value instanceof Decimal) return null;
  return `expected Decimal, got ${typeof value === 'object' ? (value as object).constructor.name : typeof value}`;
};

const plainDateValueGuard = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  if (value instanceof Temporal.PlainDate) return null;
  return `expected Temporal.PlainDate, got ${typeof value === 'object' ? (value as object).constructor.name : typeof value}`;
};

const vatSummaryValueGuard = (value: unknown): string | null => {
  if (value == null) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (value.every(isVatSummaryItem)) return null;
    return 'expected VatSummaryItem[] with Decimal-or-nullish vatRate/netAmount/vatAmount';
  }
  if (isVatSummary(value)) return null;
  return 'expected VatSummaryCurrentData or VatSummaryItem[]';
};

const INVOICE_TOTALS_OPTIONAL_DECIMAL_FIELDS = [
  'totalAmountNoVat',
  'totalAmountInclVat',
  'paidAmount',
  'accountingAmount',
  'accountingAmountNoVat',
  'accountingTotalVatAmount',
  'rounding'
] as const;

const isDocumentExchangeRate = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  Decimal.isDecimal((value as Record<string, unknown>).rate) &&
  typeof (value as Record<string, unknown>).source === 'string';

const invoiceTotalsValueGuard = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value !== 'object') {
    return `expected InvoiceTotals object, got ${typeof value}`;
  }
  const record = value as Record<string, unknown>;
  for (const key of INVOICE_TOTALS_OPTIONAL_DECIMAL_FIELDS) {
    const v = record[key];
    if (v == null) continue;
    if (!(v instanceof Decimal)) {
      return `InvoiceTotals.${key}: expected Decimal or nullish, got ${typeof v === 'object' ? (v as object).constructor.name : typeof v}`;
    }
  }
  const exchangeRate = record.accountingExchangeRate;
  if (exchangeRate != null && !isDocumentExchangeRate(exchangeRate)) {
    return 'InvoiceTotals.accountingExchangeRate: expected { rate: Decimal, source: string } or nullish';
  }
  const status = record.paymentStatus;
  if (status != null && typeof status !== 'string') {
    return `InvoiceTotals.paymentStatus: expected string or nullish, got ${typeof status}`;
  }
  return null;
};

const InvoicePurposeFormatter = createCodelistFormatter({
  namespace: 'components.invoicePurpose',
  knownValues: InvoicePurpose
});
const InvoiceTypeFormatter = createCodelistFormatter({
  namespace: 'components.invoiceType',
  knownValues: InvoiceType
});
const VatModeFormatter = createCodelistFormatter({
  namespace: 'components.vatMode',
  knownValues: VatMode
});
const PaymentMethodFormatter = createCodelistFormatter({
  namespace: 'components.paymentMethod',
  knownValues: PaymentMethod
});

export function resolveEditorConfig(
  fieldType: FieldType | undefined | null,
  options?: ResolveEditorConfigOptions
): EditorConfig {
  switch (fieldType) {
    case 'text':
      return {
        editor: TextEditor,
        formatter: TextFormatter,
        hideActions: false,
        display: 'simple',
        handleErrorDisplay: false
      };
    case 'textarea':
      return {
        editor: TextAreaEditor,
        formatter: TextFormatter,
        hideActions: false,
        display: 'simple',
        handleErrorDisplay: false
      };
    case 'date':
      return {
        editor: DateEditor,
        formatter: DateFormatter,
        hideActions: true,
        display: 'simple',
        valueGuard: plainDateValueGuard,
        handleErrorDisplay: false
      };
    case 'decimal': {
      const { currencyCode } = options ?? {};
      if (!currencyCode) {
        // The FieldEditor discriminated union prevents callers from omitting
        // `currencyCode` for `fieldType='decimal'`; this guard catches direct
        // misuse of `resolveEditorConfig`.
        throw new Error("resolveEditorConfig: fieldType='decimal' requires options.currencyCode");
      }
      const BoundDecimalFormatter: React.FC<FormatterProps> = props => (
        <DecimalFormatter {...props} currencyCode={currencyCode} />
      );
      BoundDecimalFormatter.displayName = 'DecimalFormatter(Bound)';
      return {
        editor: DecimalEditor,
        formatter: BoundDecimalFormatter,
        hideActions: false,
        display: 'simple',
        valueGuard: decimalValueGuard,
        handleErrorDisplay: false
      };
    }
    case 'address':
      return {
        editor: AddressEditor,
        formatter: AddressFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'bankingDetails':
      return {
        editor: BankingDetailsEditor,
        formatter: BankingDetailsFormatter,
        hideActions: true,
        display: 'custom',
        handleErrorDisplay: true
      };
    case 'country':
      return {
        editor: CountryEditor,
        formatter: CountryFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'currency':
      return {
        editor: CurrencyEditor,
        formatter: CurrencyFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'vatSummary':
      return {
        editor: VatSummaryEditor,
        formatter: VatSummaryFormatter,
        hideActions: true,
        display: 'custom',
        valueGuard: vatSummaryValueGuard,
        handleErrorDisplay: true
      };
    case 'invoiceTotals':
      return {
        editor: InvoiceTotalsEditor,
        formatter: InvoiceTotalsFormatter,
        hideActions: true,
        display: 'custom',
        valueGuard: invoiceTotalsValueGuard,
        handleErrorDisplay: true
      };
    case 'invoiceLines':
      return {
        editor: InvoiceLinesEditor,
        formatter: InvoiceLinesFormatter,
        hideActions: true,
        display: 'custom',
        handleErrorDisplay: true
      };
    case 'invoicePurpose':
      return {
        editor: InvoicePurposeEditor,
        formatter: InvoicePurposeFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'invoiceType':
      return {
        editor: InvoiceTypeEditor,
        formatter: InvoiceTypeFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'vatMode':
      return {
        editor: VatModeEditor,
        formatter: VatModeFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'paymentMethod':
      return {
        editor: PaymentMethodEditor,
        formatter: PaymentMethodFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    case 'partner':
      return {
        editor: PartnerInlineEditor,
        formatter: (value: unknown) => {
          const name =
            value != null && typeof value === 'object' && 'name' in value
              ? String((value as { name: unknown }).name)
              : '';
          return <span>{name || '—'}</span>;
        },
        hideActions: true,
        display: 'custom',
        handleErrorDisplay: true
      };
    case 'boolean':
      return {
        editor: BooleanEditor,
        formatter: BooleanFormatter,
        hideActions: true,
        display: 'simple',
        handleErrorDisplay: true
      };
    default:
      return {
        editor: TextEditor,
        formatter: TextFormatter,
        hideActions: false,
        display: 'simple',
        handleErrorDisplay: false
      }; // Fallback to TextEditor if no specific editor is found
  }
}
